import type { Nodes, Parent, Root } from "mdast";

import type {
  MerryMarkdownProjection,
  MerryMarkdownProjectionLeaf,
} from "./markdownSchema";

export interface MerryHighlightSegment {
  highlighted: boolean;
  id: string;
  text: string;
}

interface ProjectionCheckpoint {
  leafCount: number;
  length: number;
  partCount: number;
}

class ProjectionBuilder {
  private readonly leaves: MerryMarkdownProjectionLeaf[] = [];
  private readonly parts: string[] = [];
  private length = 0;

  checkpoint(): ProjectionCheckpoint {
    return {
      leafCount: this.leaves.length,
      length: this.length,
      partCount: this.parts.length,
    };
  }

  appendDecoration(value: string) {
    if (!value) return;
    this.parts.push(value);
    this.length += value.length;
  }

  appendLeaf(id: string, value: string) {
    if (!value) return;
    const startOffsetUtf16 = this.length;
    this.parts.push(value);
    this.length += value.length;
    this.leaves.push({
      endOffsetUtf16: this.length,
      id,
      startOffsetUtf16,
      text: value,
    });
  }

  hasContentSince(checkpoint: ProjectionCheckpoint) {
    return this.length > checkpoint.length;
  }

  rollback(checkpoint: ProjectionCheckpoint) {
    this.parts.length = checkpoint.partCount;
    this.leaves.length = checkpoint.leafCount;
    this.length = checkpoint.length;
  }

  toProjection(): MerryMarkdownProjection {
    return {
      leaves: this.leaves,
      schema: "conversation_selectable_text.v1",
      text: this.parts.join(""),
    };
  }
}

const highlightPattern = /==([^=\n]+)==/g;

export function splitMerryHighlightText(
  value: string,
  path: string,
): ReadonlyArray<MerryHighlightSegment> {
  if (!value.includes("==")) {
    return [{ highlighted: false, id: `${path}:text:0`, text: value }];
  }
  const segments: MerryHighlightSegment[] = [];
  let cursor = 0;
  let index = 0;
  for (const match of value.matchAll(highlightPattern)) {
    const matchIndex = match.index;
    const highlighted = match[1];
    if (matchIndex === undefined || highlighted === undefined) continue;
    if (matchIndex > cursor) {
      segments.push({
        highlighted: false,
        id: `${path}:text:${index}`,
        text: value.slice(cursor, matchIndex),
      });
      index += 1;
    }
    segments.push({
      highlighted: true,
      id: `${path}:text:${index}`,
      text: highlighted,
    });
    index += 1;
    cursor = matchIndex + match[0].length;
  }
  if (cursor < value.length || segments.length === 0) {
    segments.push({
      highlighted: false,
      id: `${path}:text:${index}`,
      text: value.slice(cursor),
    });
  }
  return segments;
}

function appendChildren(
  node: Parent,
  path: string,
  builder: ProjectionBuilder,
) {
  node.children.forEach((child, index) => {
    appendNode(child as Nodes, `${path}.${index}`, builder);
  });
}

function appendSeparatedChildren(
  node: Parent,
  path: string,
  separator: string,
  builder: ProjectionBuilder,
) {
  let visibleCount = 0;
  node.children.forEach((child, index) => {
    const before = builder.checkpoint();
    if (visibleCount > 0) builder.appendDecoration(separator);
    const contentStart = builder.checkpoint();
    appendNode(child as Nodes, `${path}.${index}`, builder);
    if (builder.hasContentSince(contentStart)) {
      visibleCount += 1;
    } else {
      builder.rollback(before);
    }
  });
}

function appendNode(node: Nodes, path: string, builder: ProjectionBuilder) {
  switch (node.type) {
    case "root":
      appendSeparatedChildren(node, path, "\n\n", builder);
      return;
    case "heading":
    case "paragraph":
    case "strong":
    case "emphasis":
    case "delete":
    case "link":
    case "linkReference":
    case "tableCell":
      appendChildren(node, path, builder);
      return;
    case "blockquote":
    case "footnoteDefinition":
      appendSeparatedChildren(node, path, "\n\n", builder);
      return;
    case "list":
    case "listItem":
    case "table":
      appendSeparatedChildren(node, path, "\n", builder);
      return;
    case "tableRow":
      appendSeparatedChildren(node, path, "\t", builder);
      return;
    case "text":
      for (const segment of splitMerryHighlightText(node.value, path)) {
        builder.appendLeaf(segment.id, segment.text);
      }
      return;
    case "inlineCode":
      builder.appendLeaf(`${path}:inline-code`, node.value);
      return;
    case "code":
      builder.appendLeaf(`${path}:code`, node.value);
      return;
    case "html":
      builder.appendLeaf(`${path}:html`, node.value);
      return;
    case "image":
    case "imageReference": {
      const label = node.alt?.trim() || "未命名图片";
      builder.appendLeaf(`${path}:image`, `[图片：${label}]`);
      return;
    }
    case "footnoteReference":
      builder.appendLeaf(`${path}:footnote`, `[${node.label ?? node.identifier}]`);
      return;
    case "break":
      builder.appendDecoration("\n");
      return;
    case "definition":
    case "thematicBreak":
    case "yaml":
      return;
  }
}

export function projectMerryMarkdown(root: Root) {
  const builder = new ProjectionBuilder();
  appendNode(root, "root", builder);
  return builder.toProjection();
}
